'use client';

import React, { useMemo, useState } from "react";
import { UserPlus, Loader2, Clock } from "lucide-react";
import { useTeam } from "./internals";
import { useTeamData } from "./hooks/useTeamData";
import { useInvites } from "./hooks/useInvites";
import { MemberRow } from "./components/MemberRow";
import { InviteRow } from "./components/InviteRow";
import { EmptyState } from "./components/EmptyState";
import { InviteDrawer } from "./components/InviteDrawer";
import { MemberEditDrawer } from "./components/MemberEditDrawer";
import { TransferOwnershipDialog } from "./components/TransferOwnershipDialog";
import { RemoveMemberDialog } from "./components/RemoveMemberDialog";
import { AuditLog } from "./components/AuditLog";

export const TeamPage = () => {
  const { copy, currentUser } = useTeam();
  const { members, loading, error, refresh } = useTeamData();
  const { invites, loading: invitesLoading, refresh: refreshInvites } = useInvites();
  const [tab, setTab] = useState("all");

  // Overlay state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [transferMember, setTransferMember] = useState(null);
  const [removeMember, setRemoveMember] = useState(null);

  const canInvite = ["owner", "admin", "manager"].includes(currentUser.role);
  const canViewAudit = ["owner", "admin"].includes(currentUser.role);

  const TABS = useMemo(() => {
    const base = [
      { key: "all", label: copy.tab_all },
      { key: "owners", label: copy.tab_owners },
      { key: "pending", label: copy.tab_pending },
    ];
    if (canViewAudit) base.push({ key: "activity", label: copy.tab_activity });
    return base;
  }, [copy, canViewAudit]);

  const counts = useMemo(
    () => ({
      all: members.length,
      owners: members.filter((m) => m.role === "owner").length,
      pending: invites.length,
    }),
    [members, invites]
  );

  const memberList = tab === "owners" ? members.filter((m) => m.role === "owner") : members;

  const afterMemberChange = () => refresh();

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Header */}
      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">
            {copy.page_title}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">{copy.page_subtitle}</p>
        </div>
        {canInvite && (
          <button
            type="button"
            data-testid="team-invite-btn"
            onClick={() => setInviteOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            <UserPlus size={15} />
            <span className="hidden sm:inline">{copy.cta_invite}</span>
          </button>
        )}
      </header>

      {/* Tabs */}
      <nav data-testid="team-tabs" className="mb-5 flex gap-1 border-b border-zinc-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`team-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`relative -mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-zinc-900 text-zinc-900"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
          >
            {t.label}
            {counts[t.key] !== undefined && (
              <span
                data-testid={`team-tab-${t.key}-count`}
                className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-zinc-100 px-1.5 text-[11px] font-semibold text-zinc-700"
              >
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Body */}
      {tab === "activity" ? (
        <AuditLog />
      ) : tab === "pending" ? (
        invitesLoading ? (
          <Loader testId="team-invites-loading" label="Loading invites…" />
        ) : invites.length === 0 ? (
          <div
            data-testid="pending-empty-state"
            className="rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-12 text-center"
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
              <Clock size={22} className="text-zinc-500" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-zinc-900">No pending invites</h3>
            <p className="mt-1 text-sm text-zinc-500">
              People you invite will wait here until they join.
            </p>
            {canInvite && (
              <button
                type="button"
                data-testid="pending-invite-btn"
                onClick={() => setInviteOpen(true)}
                className="mt-5 inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                {copy.cta_invite}
              </button>
            )}
          </div>
        ) : (
          <ul data-testid="pending-list" className="space-y-2">
            {invites.map((inv) => (
              <li key={inv.id}>
                <InviteRow invite={inv} onRevoked={refreshInvites} />
              </li>
            ))}
          </ul>
        )
      ) : loading ? (
        <Loader testId="team-loading" label="Loading team…" />
      ) : error ? (
        <div
          data-testid="team-error"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700"
        >
          {error}
          <button
            type="button"
            onClick={refresh}
            className="ml-3 text-red-800 underline underline-offset-2 hover:no-underline"
          >
            Try again
          </button>
        </div>
      ) : memberList.length === 0 ? (
        <EmptyState onInvite={() => setInviteOpen(true)} />
      ) : (
        <ul data-testid="team-list" className="space-y-2">
          {memberList.map((m) => (
            <li key={m.id}>
              <MemberRow member={m} onClick={() => setEditMember(m)} />
            </li>
          ))}
        </ul>
      )}

      {/* Overlays */}
      <InviteDrawer open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={refreshInvites} />

      <MemberEditDrawer
        open={!!editMember}
        member={editMember}
        onClose={() => setEditMember(null)}
        onChanged={afterMemberChange}
        onTransfer={(m) => {
          setEditMember(null);
          setTransferMember(m);
        }}
        onRemove={(m) => {
          setEditMember(null);
          setRemoveMember(m);
        }}
      />

      <TransferOwnershipDialog
        open={!!transferMember}
        member={transferMember}
        onClose={() => setTransferMember(null)}
        onDone={refresh}
      />

      <RemoveMemberDialog
        open={!!removeMember}
        member={removeMember}
        members={members}
        onClose={() => setRemoveMember(null)}
        onDone={refresh}
      />
    </main>
  );
};

const Loader = ({ testId, label }) => (
  <div
    data-testid={testId}
    className="flex items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-sm text-zinc-500"
  >
    <Loader2 size={16} className="mr-2 animate-spin" />
    {label}
  </div>
);
